/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { ActionPlanStatus, DemandAnalysis, DemandAnalysisQuestion, DemandAnalysisQuestionStatus, DemandAnalysisStatus, DemandLevel } from '../../../api/schemas/schema'
import { t } from '../../../i18n/i18n'
import { makeStyles, tokens, mergeClasses, Divider, Switch } from '@fluentui/react-components'
import { useSearchParams } from 'react-router-dom'
import { filterQuestions, getCompletedProcesses, getCurrentAnalysisColumns, isProcessOwnerOwnerOrSystemAdmin } from '../../../helpers/demandAssessmentHelper'
import { Text } from '../../common/text/Text'
import { Avatar } from '../../common/avatar/Avatar'
import { BadgeAnalysisQuestion } from '../BadgeAnalysisQuestion'
import { ModalAnalysisQuestion } from './ModalAnalysisQuestion'
import { Processfilter } from '../ProcessFilter'
import { Title } from '../../common/text/Title'
import { CustomList } from '../../common/list/CustomList'
import { ModalAnalysisReport } from './ModalAnalysisReport'
import { RTFOutput } from '../../common/outputs/RTFOutput'
import { SummaryRow } from '../../common/list/SummaryRow'
import { getFullName } from '../../../helpers/accountHelper'
import { IItemState } from '../../../interfaces/IItemState'
import { DataContext } from '../../../context/DataContext'
import { Loader } from '../../common/spinner/Loader'

const useStyles = makeStyles({
   hideInPhone: {
      '@media screen and (max-width: 640px)': {
         display: 'none',
      },
   },
   phoneListWrapper: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'flex',
         flexDirection: 'column',
         gap: tokens.spacingVerticalXXL,
      },
   },
   spaceBtw: {
      display: 'flex',
      justifyContent: 'space-between',
   },
   flex: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      alignItems: 'center',
   },
   phoneListItem: {
      '& > :last-child': {
         marginTop: tokens.spacingVerticalXS,
      },
      '& p': {
         margin: '0 auto',
      },
   },
   padding: {
      padding: `0 ${tokens.spacingHorizontalXL}`,
   },
   hidden: {
      visibility: 'hidden',
   },
   header: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'block',
         '& section': {
            display: 'flex',
            justifyContent: 'space-between',
            '& h3': {
               margin: 0,
            },
         },
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '5%',
            minWidth: '10px',
         },
         ':nth-child(2)': {
            width: '100%',
         },
         ':nth-child(3)': {
            width: '20%',
         },
         ':nth-child(4)': {
            width: '20%',
         },
         ':nth-child(5)': {
            width: '10%',
            minWidth: '100px',
         },
      },
   },
})

type CurrentDemandAnalysisProps = {
   analysisState: IItemState<DemandAnalysis>
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<DemandAnalysis>>>
   questionsLoading?: boolean
}
export const CurrentDemandAnalysis = ({ analysisState, setAnalysisState, questionsLoading }: CurrentDemandAnalysisProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const { item: analysis } = analysisState
   const classes = useStyles()
   const processes: string[] = [t('AllProcesses')]
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedProcess = searchParams.get('process')
   const [filteredQuestions, setFilteredQuestions] = useState<DemandAnalysisQuestion[]>(filterQuestions(selectedProcess, analysis.questions))
   const [selectedItem, setSelectedItem] = useState<DemandAnalysisQuestion>()
   const [showHeadings, setShowHeadings] = useState<boolean>(true)
   const [isQuestionAnalysisDisabled, setIsQuestionAnalysisDisabled] = useState<boolean>(false)
   const [questionActionPlanStatus, setQuestionActionPlanStatus] = useState<ActionPlanStatus>(null)

   const setProcess = (process: string) => {
      searchParams.set('process', process)
      setSearchParams(searchParams)
   }

   useEffect(() => {
      if (searchParams.get('process') === null || !processes.find((x) => x === searchParams.get('process'))) {
         setProcess(processes[0])
      }
   }, [window.location.search])

   useEffect(() => {
      setFilteredQuestions(filterQuestions(selectedProcess, analysis.questions))
   }, [selectedProcess, analysis.questions])

   useEffect(() => {
      if (analysisState.item) {
         if (!isProcessOwnerOwnerOrSystemAdmin(analysisState, selectedItem, userAccount)) {
            setIsQuestionAnalysisDisabled(true)
         } else {
            setIsQuestionAnalysisDisabled(false)
         }
      }
   }, [selectedItem])

   ;(analysis.questions ?? [])
      .sort((a, b) => a.number - b.number)
      .forEach((x) =>
         x.processes.forEach((p) => {
            if (!processes.some((c) => c === p)) {
               processes.push(p)
            }
         })
      )
   // Processfiltret ska ligga i bokstavsordning, med "Alla processer" kvar först
   processes.splice(1, processes.length, ...processes.slice(1).sort((a, b) => a.localeCompare(b)))

   const completedProcesses = getCompletedProcesses(analysis.questions, processes)

   const onRowClick = (question: DemandAnalysisQuestion) => {
      const questionActionPlan = (analysis.actionPlans ?? []).find((x) => x.demandAnalysisQuestionId === question.id)
      const questionActionPlanStatus = questionActionPlan ? questionActionPlan.status : null

      setQuestionActionPlanStatus(questionActionPlanStatus)
      setSelectedItem(question)
   }

   if (questionsLoading) return <Loader />

   return (
      <>
         <header className={classes.header}>
            <section>
               <Title as="h3">{t('DemandAnalysis')}</Title>
            </section>
            <Divider appearance="brand" />
         </header>
         <article>
            <Processfilter
               setProcess={setProcess}
               processes={processes}
               completedProcesses={completedProcesses}
               title={t('DemandAnalysis')}
               action={
                  <span className={classes.flex}>
                     <Switch
                        label={t('ShowHeadings')}
                        checked={showHeadings}
                        onChange={(_, data) => setShowHeadings(data.checked)}
                        className={classes.hideInPhone}
                     />
                     <ModalAnalysisReport questions={filteredQuestions} demandAnalysisProcessOwners={analysis.demandAnalysisProcessOwners} />
                  </span>
               }
            />
            {filteredQuestions.length > 0 ? (
               <>
                  <SummaryRow
                     summaryFields={[
                        {
                           label: t('AllAnswered'),
                           text: `${filteredQuestions.filter((x) => x.status !== DemandAnalysisQuestionStatus.NotAnswered).length} / ${
                              filteredQuestions.length
                           }`,
                        },
                     ]}
                     className={classes.hideInPhone}
                  />
                  <div className={mergeClasses(classes.padding, classes.hideInPhone)}>
                     <CustomList
                        onRowClick={onRowClick}
                        rowClassName={classes.row}
                        columns={getCurrentAnalysisColumns(analysis.demandAnalysisProcessOwners, showHeadings)}
                        items={filteredQuestions}
                     />
                  </div>
                  <section className={classes.phoneListWrapper}>
                     {filteredQuestions.map((x, i) => {
                        const processOwner = analysis.demandAnalysisProcessOwners.find((po) => po.process.text === x.processes[0])
                        return (
                           <div key={i} className={classes.phoneListItem} onClick={() => onRowClick(x)}>
                              <div className={classes.spaceBtw}>
                                 <span className={classes.flex}>
                                    <Text weight="semibold">{x.number}</Text>
                                    <Text>{x.processes}</Text>
                                 </span>
                                 <span className={classes.flex}>
                                    <Avatar
                                       name={processOwner && processOwner.userAccount ? getFullName(processOwner.userAccount) : processOwner?.ownerFreeText}
                                       onlyCircle
                                       color="brand"
                                    />
                                    <BadgeAnalysisQuestion status={x.status} />
                                 </span>
                              </div>
                              {showHeadings && x.demandUrlText ? (
                                 <Text weight={x.demandLevel === DemandLevel.Two ? 'bold' : 'regular'} block>
                                    {x.demandUrlText}
                                 </Text>
                              ) : null}
                              <RTFOutput text={x.text} fontSize={300} noMargin ellipsis />
                           </div>
                        )
                     })}
                  </section>
               </>
            ) : null}
            <ModalAnalysisQuestion
               analysisDisabled={isQuestionAnalysisDisabled || analysis.status === DemandAnalysisStatus.Completed}
               item={selectedItem}
               questionActionPlanStatus={questionActionPlanStatus}
               close={() => setSelectedItem(undefined)}
               setItems={setFilteredQuestions}
               setAnalysisState={setAnalysisState}
            />
         </article>
      </>
   )
}
